import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Pagination,
} from "@mui/material";
import { API_GET_STUDENTS,API_STATUS_UPDATE } from "../Config/ApiUrlConstant";
import { useAuth } from "../AuthContext";
import AddEditStudent from "./AddEditStudent";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [search, setSearch] = useState({
    student_no: "",
    student_name: "",
    father_name: "",
    class_name: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;
  const [openDialog, setOpenDialog] = useState(false);
  const [editStudent, setEditStudent] = useState(null);

  const { logout } = useAuth();

  // 🔹 Fetch students from API
  const getAllStudents = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_GET_STUDENTS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }

      const responseData = await response.json();
      const data = responseData.data || [];
      setStudents(data);
      setFilteredStudents(data);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  useEffect(() => {
    getAllStudents();
  }, []);

  // 🔹 Filter logic including Student Name & Father Name
  useEffect(() => {
    const filtered = students.filter((student) => {
      const no = (student.student_no || "").toLowerCase();
      const name = (student.student_name || "").toLowerCase();
      const fatherName = (student.student_father_name || "").toLowerCase();
      const className = (student.class || "").toLowerCase();

      return (
        no.includes(search.student_no.toLowerCase()) &&
        name.includes(search.student_name.toLowerCase()) &&
        fatherName.includes(search.father_name.toLowerCase()) &&
        className.includes(search.class_name.toLowerCase())
      );
    });

    setFilteredStudents(filtered);
    setCurrentPage(1);
  }, [search, students]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
  };

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleOpenDialog = (student = null) => {
    setEditStudent(student);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditStudent(null);
  };

  // Add inside StudentManagement component
  const handleToggleStatus = async (student) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_STATUS_UPDATE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body:JSON.stringify({ is_active: student.is_active === 1 ? 0 : 1,id:student.id }),
      });
      const result = await response.json();
      if (response.ok) {
        getAllStudents(); // refresh the list
      } else {
        alert(result.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating student status:", error);
    }
  };

  return (
    <Box p={2}>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h5">Student List</Typography>
        </Grid>
        <Grid item xs={12} sm="auto">
          <Button variant="contained" onClick={() => handleOpenDialog(null)}>
            Add New Student
          </Button>
        </Grid>
      </Grid>

      {/* 🔹 Search Filters */}
      <Grid container spacing={2} mb={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search by Student No"
            name="student_no"
            value={search.student_no}
            onChange={handleSearchChange}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search by Student Name"
            name="student_name"
            value={search.student_name}
            onChange={handleSearchChange}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search by Father Name"
            name="father_name"
            value={search.father_name}
            onChange={handleSearchChange}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search by Class"
            name="class_name"
            value={search.class_name}
            onChange={handleSearchChange}
            size="small"
          />
        </Grid>
      </Grid>

      {/* 🔹 Student Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student No</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell>Father Name</TableCell>
              <TableCell>Mother Name</TableCell>
              <TableCell>Magic Charge</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentStudents.length > 0 ? (
              currentStudents.map((student, index) => (
                <TableRow key={student.id || index}>
                  <TableCell>{indexOfFirstStudent + index + 1}</TableCell>
                  <TableCell>{student.student_no}</TableCell>
                  <TableCell>{student.class}</TableCell>
                  <TableCell>{student.student_name}</TableCell>
                  <TableCell>{student.student_father_name}</TableCell>
                  <TableCell>{student.student_mother_name}</TableCell>
                  <TableCell>{student.magic_charge}</TableCell>
                  <TableCell>
                    {student.is_active === 1 ? "Active" : "Inactive"}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                      onClick={() => handleOpenDialog(student)}
                      style={{ width: "50px" }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color={student.is_active === 1 ? "error" : "success"}
                      style={{ width: "100px" }}
                      onClick={() => handleToggleStatus(student)}
                    >
                      {student.is_active === 1 ? "Deactivate" : "Activate"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No students found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 🔹 Pagination */}
      {totalPages > 1 && (
        <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* 🔹 Add/Edit Student Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editStudent ? "Edit Student" : "Add Student"}
            </Typography>
            <IconButton onClick={handleCloseDialog} style={{width:'120px'}}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <AddEditStudent
            studentData={editStudent}
            onSuccess={() => {
              getAllStudents();
              handleCloseDialog();
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StudentManagement;
